import type { ServiceProvider, ServiceVerificationStatus } from '../types/firestore';

type ServiceVerificationInput = Partial<ServiceProvider> | null | undefined;

const normalizeStatus = (value: unknown): ServiceVerificationStatus | null => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (!normalized) return null;
  if (
    normalized === 'profile_incomplete' ||
    normalized === 'incomplete' ||
    normalized === 'draft'
  ) {
    return 'profile_incomplete';
  }
  if (normalized === 'pending_approval' || normalized === 'pending') {
    return 'pending_approval';
  }
  if (normalized === 'approved' || normalized === 'active' || normalized === 'verified') {
    return 'approved';
  }
  if (
    normalized === 'changes_requested' ||
    normalized === 'rejected' ||
    normalized === 'requires_changes'
  ) {
    return 'changes_requested';
  }
  return null;
};

const hasNonEmptyString = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const hasValidLocation = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  const latitude = [candidate.latitude, candidate.lat, candidate._latitude].find(
    (item) => typeof item === 'number' && Number.isFinite(item)
  ) as number | undefined;
  const longitude = [candidate.longitude, candidate.lng, candidate.lon, candidate._longitude].find(
    (item) => typeof item === 'number' && Number.isFinite(item)
  ) as number | undefined;

  if (latitude === undefined || longitude === undefined) return false;
  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

export const isServiceProfileComplete = (provider: ServiceVerificationInput): boolean => {
  if (!provider) return false;
  const specializations = Array.isArray(provider.specializations)
    ? provider.specializations.filter((value) => hasNonEmptyString(value))
    : [];

  return (
    hasNonEmptyString(provider.serviceName) &&
    hasNonEmptyString(provider.description) &&
    hasNonEmptyString(provider.phoneNumber) &&
    hasNonEmptyString(provider.address?.street) &&
    hasNonEmptyString(provider.address?.city) &&
    hasNonEmptyString(provider.address?.postalCode) &&
    specializations.length > 0 &&
    hasValidLocation(provider.location)
  );
};

export const resolveServiceVerificationStatus = (
  provider: ServiceVerificationInput
): ServiceVerificationStatus => {
  if (!provider) {
    return 'profile_incomplete';
  }

  const explicitStatus = normalizeStatus(provider.verificationStatus);
  if (explicitStatus) {
    return explicitStatus;
  }

  if (provider.isVerified === true) {
    return 'approved';
  }

  if (hasNonEmptyString(provider.verificationReviewReason)) {
    return 'changes_requested';
  }

  if (isServiceProfileComplete(provider)) {
    return 'pending_approval';
  }

  return 'profile_incomplete';
};

export const isServiceApprovedForOperations = (provider: ServiceVerificationInput): boolean =>
  resolveServiceVerificationStatus(provider) === 'approved';

export const getServiceVerificationReviewReason = (
  provider: ServiceVerificationInput
): string | null => {
  if (!provider) return null;
  const directReason = String(provider.verificationReviewReason || '').trim();
  if (directReason) return directReason;

  const legacyRejectedReason = String(
    (provider as Record<string, unknown>).rejectedReason || ''
  ).trim();
  if (legacyRejectedReason) return legacyRejectedReason;

  return null;
};
